import {Divider, Paper} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import { useTranslation } from 'react-i18next';
import {Tooltip} from "@mui/material";
import HistoricalChart from "./historicalChart";
import {getFlagCountry} from "../utils/miscFunctions";
import Draggable from "react-draggable";
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloseButton from "./closeButton";
import FloodDayByDayChart from "./floodDayByDayChart";
import {extractDateParts, formatDateFlood} from "../utils/dateFunctions";
import {numberWithCommas} from "../utils/formatFunctions";
import {findDepthByBand} from "../utils/depth";
import Legend from "./legend";
import {
    FLOOD_DEPTH_GRADIENT_COLORS,
    FLOOD_RISK_SUFFIX,
    HEIGHT_FLOOD_OVERLAY, URL_FLOOD_RISK_PARAMETERS, URL_ROOT, URL_ROOT_FLE,
    WIDTH_FLOOD_OVERLAY
} from "../constants/constants";
import {formatDateFloodShort} from "../utils/dateFunctions";
import {payout} from "../utils/payoutFunctions";
import {DataContext} from "../contexts/DataContext";
import {useAuthenticator} from "@aws-amplify/ui-react";
import {DataStore} from "@aws-amplify/datastore";
import {RiskParameter} from "../models";
import {extractHierarchy, createADM1Object, generateBandArray} from "../utils/adm";

export default function FloodOverlay(props) {

    const {data, selectedFlood, countryStochasticPopulationData, countryStochasticEconomicData, width, height} = props;

    const [year, month, day] = extractDateParts(data.start_date);

    const { t } = useTranslation();

    const {
        attachmentPoint,
        aggregateLimit,
        cedingPercentage,
        exhaustionPoint,
    } = useContext(DataContext);

    const AP = attachmentPoint
    const AL = aggregateLimit
    const CP = cedingPercentage
    const EP = exhaustionPoint

    const countryCode = selectedFlood.match(/\/(\w{3})\//)[1];

    const { authStatus } = useAuthenticator(context => [context.authStatus]);
    const { user, signOut } = useAuthenticator((context) => [context.user]);

    const readSimulation = authStatus === 'authenticated' && user.signInUserSession.accessToken.payload["cognito:groups"].some(e => ['simulation','payout','admin'].includes(e))
    const readPayout = authStatus === 'authenticated' && user.signInUserSession.accessToken.payload["cognito:groups"].some(e => ['payout','admin'].includes(e))

    const [open, setOpen] = useState(true);
    const [riskParameter, setRiskParameter] = useState(null)

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        setOpen(true)
    }, [selectedFlood])

    useEffect(() => {
        if (readPayout) {
            const url = URL_ROOT_FLE + 'data/risk_parameters/risk_parameters.json'
            if (data.adm0_eco_max) {
                const country = data.adm0_eco_max[0].ADM0_NAME
                const year = Number(data.ongoing_event_year);
                fetchDataAndFilter(url, country, year)
            }
            //fetchData() // REPLACE WITH
        }
    },[])

/*    async function fetchData() {
        const records = await DataStore.query(RiskParameter);

        const floodRecords = records.filter(item => item.country.endsWith(FLOOD_RISK_SUFFIX));

        // Apply .replace('_flood', '') to each "country" field
        const modifiedFloodRecords = floodRecords.map(item => ({
            ...item,
            country: item.country.replace(FLOOD_RISK_SUFFIX, '')
        }));

        setRiskParameter(modifiedFloodRecords);
    }*/

    async function fetchData() {
        const records = await DataStore.query(RiskParameter);
        if (data.adm0_eco_max) {
            const country = data.adm0_eco_max[0].ADM0_NAME + FLOOD_RISK_SUFFIX; // Assign country from data object
            const year = Number(data.ongoing_event_year); // Assign year from data object

            const floodRecord = records.filter(item => item.country === country && item.year === year);

            // Apply .replace('_flood', '') to each "country" field
            const modifiedFloodRecord = floodRecord.map(item => ({
                ...item,
                country: item.country.replace(FLOOD_RISK_SUFFIX, '')
            }));

            setRiskParameter(modifiedFloodRecord[0]);
        }

    }

    async function fetchDataAndFilter(url, country, year) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch data from ${url}`);
            }

            const data_received = await response.json();

            // Filter the data by country and year
            //const filteredData = data_received.filter(item => item.country === country && item.year === year);

            const filteredData = data_received.find(obj => obj.country === country && obj.year === year);

            console.log('filteredData', filteredData)

            if (filteredData.length === 0) {
                throw new Error(`No data found for ${country} in ${year}`);
            }

            // Return the first matching object (assuming there's only one match)
            setRiskParameter(filteredData);
        } catch (error) {
            console.error(error);
            return null; // Handle the error as needed
        }
    }


    const total_population_band_1 = data.population_total ? numberWithCommas(data.population_total[0].band_1) : numberWithCommas(data.peak_population.population[0].band_1);
    const total_population_band_5 = data.population_total ? numberWithCommas(data.population_total[1].band_5) : numberWithCommas(data.peak_population.population[0].band_5);
    const total_population_band_11 = data.population_total ? numberWithCommas(data.population_total[2].band_11) : numberWithCommas(data.peak_population.population[0].band_11);

    const populationAdm1 = createADM1Object(data.adm1_max);

    // const totalPopulation = {
    //     band_1: data.population_total[0].band_1,
    //     band_5: data.population_total[1].band_5,
    //     band_11: data.population_total[2].band_11,
    // }

    const totalPopulation = {
        'adm0': generateBandArray(data.adm0_max[0].band_1, data.adm0_max[0].band_5, data.adm0_max[0].band_11),
        'adm1': createADM1Object(data.adm1_max),
    }


    const total_economic_band_1 = data.economic_total ? numberWithCommas(data.economic_total[0].band_1, '$') : null;
    const total_economic_band_5 = data.economic_total ? numberWithCommas(data.economic_total[1].band_5, '$') : null;
    const total_economic_band_11 = data.economic_total ? numberWithCommas(data.economic_total[2].band_11, '$') : null;

    let totalEconomic;

    if (data.economic_total) {

        const economicAdm1 = createADM1Object(data.adm1_eco_max, false);

        totalEconomic = {
            'adm0': {
            band_1: data.economic_total[0].band_1,
            band_5: data.economic_total[1].band_5,
            band_11: data.economic_total[2].band_11,
        },
            'adm1': economicAdm1,
        }
    }

    const maxSeverityIndex1m = data.day_by_day.reduce((maxValue, item) => {
        return Math.max(maxValue, item.stats.severity_index_1m);
    }, -Infinity);

    return (
        <div>
            {data &&
                <Draggable>
                    <div className='overlay' style={{width: width, left: '5vw', top: '40vh'}}>
                        {
                            open &&
                            <Paper
                                elevation={15}
                                sx={{backgroundColor: 'rgba(234,234,234,0.72)'}}
                            >
                                {/*                        <IconButton className='close-button'>
                            <CloseIcon />
                        </IconButton>*/}
                                {/*{props.adm_name}*/}
                                <div style={{overflow: 'scroll', maxHeight: height}}>
                                    <CloseButton handleClose={handleClose}/>
                                    <div className="exposure-info">
                                        <Tooltip placement={'top'} title={t('flood_overlay.start_date.helper')}>
                                            <div><strong>{t('flood_overlay.start_date.title')}:</strong>&nbsp;<span id="start_date" className={'start_date'}>{day} {t(`constants.months.${month - 1}`)} {year}</span></div>
                                        </Tooltip>
                                        <Tooltip placement={'top'} title={t('flood_overlay.duration.helper')}>
                                            <div><strong>{t('flood_overlay.duration.title')}:</strong>&nbsp;<span id="duration" className={'duration'}>{data.total_days_event} {t('flood_overlay.duration.unit')}</span></div>
                                        </Tooltip>
                                        <Tooltip placement={'top'} title={t('flood_overlay.max_severity_index.helper')}>
                                            {/*//TODO: remove that fix once the backend is updated*/}
                                            {/*<div><strong>{t('flood_overlay.max_severity_index.title')}:</strong>&nbsp;<span id="max_severity_index" className={'max_severity_index'}>{data.peak_flood.stats.severity_index_1m}</span></div>*/}
                                            <div><strong>{t('flood_overlay.max_severity_index.title')}:</strong>&nbsp;<span id="max_severity_index" className={'max_severity_index'}>{maxSeverityIndex1m}</span></div>
                                        </Tooltip>
{/*                                        {
                                            data.peak_flood.stats.severity_index_1km2 &&
                                            <Tooltip placement={'top'} title={t('flood_overlay.severity_index_1km2.helper')}>
                                                <div><strong>{t('flood_overlay.severity_index_1km2.title')}:</strong>&nbsp;<span id="max_severity_index_1km2" className={'max_severity_index_1km2'}>{data.peak_flood.stats.severity_index_1km2}</span></div>
                                            </Tooltip>
                                        }*/}
{/*                                        {
                                            data.peak_flood.stats.total_water_km3 &&
                                            <Tooltip placement={'top'} title={t('flood_overlay.total_water_km3.helper')}>
                                                <div><strong>{t('flood_overlay.total_water_km3.title')}:</strong>&nbsp;<span id="max_total_water_km3" className={'max_total_water_km3'}>{data.peak_flood.stats.total_water_km3}</span></div>
                                            </Tooltip>
                                        }*/}
                                        <Tooltip placement={'top'} title={t('flood_overlay.max_flooded_area.helper')}>
                                            <div><strong>{t('flood_overlay.max_flooded_area.title')}:</strong>&nbsp;<span id="max_flooded_area" className={'max_flooded_area'}>{data.peak_flood.stats.flooded_area_km2} km2</span></div>
                                        </Tooltip>
                                        <Tooltip placement={'top'} title={t('flood_overlay.max_depth.helper')}>
                                            <div><strong>{t('flood_overlay.max_depth.title')}:</strong>&nbsp;<span id="max_depth" className={'max_depth'}>{findDepthByBand(data.peak_flood.stats.max)} m</span></div>
                                        </Tooltip>
                                        <Tooltip placement={'top'} title={t('flood_overlay.worst_day.helper')}>
                                            <div><strong>{t('flood_overlay.worst_day.title')}:</strong>&nbsp;<span id="worst_day" className={'worst_day'}>{formatDateFloodShort(data.start_date, data.peak_population.day + 1)}</span></div>
                                        </Tooltip>
                                        <Tooltip placement={'top'} title={t('flood_overlay.max_population.helper')}>
                                            <div><strong>{t('flood_overlay.max_population.title')}:</strong>&nbsp;<span id="max_population" className={'max_population'}> {total_population_band_5} </span></div>
                                        </Tooltip>
                                        {total_economic_band_5 && total_economic_band_11 &&
                                            <Tooltip placement={'top'} title={t('flood_overlay.max_economic.helper')}>
                                                <div><strong>{t('flood_overlay.max_economic.title')}:</strong>&nbsp;
                                                    <span id="max_economic"
                                                          className={'max_economic'}>{total_economic_band_5} </span>
                                                </div>
                                            </Tooltip>
                                        }
                                        {
                                            readSimulation && (AP != 0) && (EP != 0) && (CP != 0) && (AL != 0) && data.economic_total && data.economic_total[1].band_5 > 0 &&
                                            <div className={'storm-details'}>
                                                <Divider />
                                                <Tooltip placement={'top'} title={t('storm_overlay.simulation.helper')}>
                                                    <div>
                                                        {t('storm_overlay.simulation.title')}
                                                    </div>
                                                </Tooltip>
                                                <div className={'countries-exposed'}>
                                                    <div className={'country-exposed'}>
                                                        {numberWithCommas(
                                                            payout(
                                                                data.economic_total[1].band_5,
                                                                AP,
                                                                EP,
                                                                CP / 100,
                                                                AL), '$')
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                        {
                                            readPayout && riskParameter && data.economic_total && data.economic_total[1].band_5 > 0 &&
                                            <div className={'storm-details'}>
                                                <Divider />
                                                <Tooltip placement={'top'} title={t('storm_overlay.payout.helper')}>
                                                    <div>
                                                        {t('storm_overlay.payout.title')}
                                                    </div>
                                                </Tooltip>
                                                <div className={'countries-exposed'} >
                                                    <div className={'country-exposed'}>
                                                        {numberWithCommas(
                                                            payout(
                                                                data.economic_total[1].band_5,
                                                            riskParameter.attachmentPoint,
                                                            riskParameter.exhaustionPoint,
                                                            riskParameter.cedingPercentage,
                                                            riskParameter.aggregateLimit), '$')
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        }
    {/*                                    <div>
                                            Start date: {day} {t(`constants.months.${month}`)} {year}
                                        </div>
                                        <div>
                                            Duration: {data.total_days_event} days
                                        </div>
                                        <div>
                                            Max Severity Index: {data.peak_flood.stats.severity_index_1m}
                                        </div>
                                        <div>
                                            Max Flooded Area (km2): {data.peak_flood.stats.flooded_area_km2}
                                        </div>
                                        <div>
                                            Max Depth: {findDepthByBand(data.peak_flood.stats.max)} m
                                        </div>
                                        <div>
                                            Worst Day: {data.peak_flood.day}
                                        </div>
                                        <div>
                                            Max Population Affected: {numberWithCommas(data.peak_population.population[0].band_1)} / {numberWithCommas(data.peak_population.population[0].band_5)} / {numberWithCommas(data.peak_population.population[0].band_11)}
                                        </div>*/}
                                        {/*<img src={getFlagCountry(admName.adm0_name)} width="30" height="20"/>*/}
                                        {/*                                <Tooltip placement={'top'} title={t('adm_overlay.information.location.helper')}>
                                        <div><strong>{t('adm_overlay.information.location.title')}</strong>&nbsp;<span id="location" className={'location'}>{admName.adm0_name}{admName.adm1_name && ', ' + admName.adm1_name}{admName.adm2_name && ', ' + admName.adm2_name}</span></div>
                                    </Tooltip>
                                    <Tooltip placement={'top'} title={t('adm_overlay.information.population.helper')}>
                                        <div><strong>{t('adm_overlay.information.population.title')}</strong>&nbsp;<span id="population" className={'population'}>{admPopulation}</span></div>
                                    </Tooltip>
                                    <Tooltip placement={'top'} title={t('adm_overlay.information.value.helper')}>
                                        <div><strong>{t('adm_overlay.information.value.title')}</strong>&nbsp;<span id="value" className={'value'}>${admValue}</span></div>
                                    </Tooltip>
                                    <Tooltip placement={'top'} title={t('adm_overlay.information.average.helper')}>
                                        <div><strong>{t('adm_overlay.information.average.title')}</strong>&nbsp;<span>{admAvgStorm} storm(s) / year</span></div>
                                    </Tooltip>
                                    <Tooltip placement={'top'} title={t('adm_overlay.information.largest_loss.helper')}>
                                        <div><strong>{t('adm_overlay.information.largest_loss.title')}</strong>&nbsp;<span id="loss" className={'loss'}>${admBiggestLoss}</span></div>
                                    </Tooltip>*/}

                                    </div>
                                    <FloodDayByDayChart
                                        data={data}
                                        countryCode={countryCode}
                                        countryStochasticPopulationData={countryStochasticPopulationData}
                                        countryStochasticEconomicData={countryStochasticEconomicData}
                                        totalPopulation={totalPopulation}
                                        totalEconomic={totalEconomic}
                                        width={WIDTH_FLOOD_OVERLAY}
                                        height={HEIGHT_FLOOD_OVERLAY}
                                    />
                                </div>
                            </Paper>
                        }
                    </div>
                </Draggable>
            }
        </div>
    )
}

